import { Alert, Button, EmptyState, Modal, Skeleton, formatDateTime } from '@catering/ui';
import { useQuery } from '@tanstack/react-query';
import { type JSX, useState } from 'react';

import { listUserSessions } from '../../../../users/api';

export interface UserSessionsModalProps {
  userId: string;
  fullName: string;
  onRevoke: (tokenId: string) => void;
  onRevokeAll: () => void;
  onClose: () => void;
}

/**
 * `internal-users/spec.md` → "Revocación de sesiones"; `design.md` §9.4/§6.1:
 * per-row `DELETE /users/:id/sessions/:tokenId` and a global
 * `DELETE /users/:id/sessions`.
 */
export function UserSessionsModal({
  userId,
  fullName,
  onRevoke,
  onRevokeAll,
  onClose,
}: UserSessionsModalProps): JSX.Element {
  const [revokingId, setRevokingId] = useState<string | undefined>(undefined);

  const sessionsQuery = useQuery({
    queryKey: ['user-sessions', userId],
    queryFn: () => listUserSessions(userId),
  });

  return (
    <Modal open onClose={onClose} title={`Sesiones de ${fullName}`} className="max-w-lg">
      <div className="flex flex-col gap-4">
        {sessionsQuery.isError ? (
          <Alert tone="danger">No pudimos cargar las sesiones activas.</Alert>
        ) : null}

        {sessionsQuery.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : null}

        {sessionsQuery.data !== undefined && sessionsQuery.data.length === 0 ? (
          <EmptyState title="Este usuario no tiene sesiones activas." />
        ) : null}

        {sessionsQuery.data !== undefined && sessionsQuery.data.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {sessionsQuery.data.map((session) => (
              <li
                key={session.id}
                className="flex items-center justify-between gap-3 rounded-md border border-slate-200 p-3"
              >
                <div className="flex flex-col text-sm">
                  <span className="font-medium text-slate-900">
                    {session.deviceName ?? 'Dispositivo desconocido'}
                  </span>
                  <span className="text-xs text-slate-500">
                    Creada {formatDateTime(session.createdAt)}
                  </span>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  loading={revokingId === session.id}
                  onClick={() => {
                    setRevokingId(session.id);
                    onRevoke(session.id);
                  }}
                >
                  Revocar
                </Button>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="mt-2 flex items-center gap-3">
          {sessionsQuery.data !== undefined && sessionsQuery.data.length > 0 ? (
            <Button
              variant="destructive"
              onClick={() => {
                if (window.confirm(`¿Cerrar todas las sesiones de ${fullName}?`)) {
                  onRevokeAll();
                }
              }}
            >
              Revocar todas
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
